import { forwardRef, useEffect, useState } from "react";
import { ReCAPTCHA as BaseReCAPTCHA } from "react-google-recaptcha";

const RECAPTCHA_SCRIPT_SELECTOR = 'script[src*="recaptcha/api.js"]';

let activeRecaptchaLanguage = null;
let recaptchaScriptPromise = null;

const clearRecaptchaRuntime = () => {
  document.querySelectorAll(RECAPTCHA_SCRIPT_SELECTOR).forEach((script) => {
    script.remove();
  });

  delete window.grecaptcha;
  delete window.___grecaptcha_cfg;
};

const loadRecaptchaScript = (language) => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.grecaptcha && activeRecaptchaLanguage === language) {
    return Promise.resolve(window.grecaptcha);
  }

  if (recaptchaScriptPromise && activeRecaptchaLanguage === language) {
    return recaptchaScriptPromise;
  }

  activeRecaptchaLanguage = language;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    clearRecaptchaRuntime();

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${language}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
        return;
      }

      resolve(window.grecaptcha);
    };

    script.onerror = () => {
      recaptchaScriptPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };

    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
};

export const LocalizedRecaptcha = forwardRef(function LocalizedRecaptcha(
  { hl, ...props },
  ref
) {
  const [grecaptcha, setGrecaptcha] = useState(() => {
    if (
      typeof window !== "undefined" &&
      window.grecaptcha &&
      activeRecaptchaLanguage === hl
    ) {
      return window.grecaptcha;
    }

    return null;
  });

  useEffect(() => {
    let isMounted = true;

    setGrecaptcha(null);

    loadRecaptchaScript(hl)
      .then((captchaInstance) => {
        if (isMounted) {
          setGrecaptcha(captchaInstance);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [hl]);

  if (!grecaptcha) {
    return null;
  }

  return <BaseReCAPTCHA ref={ref} grecaptcha={grecaptcha} hl={hl} {...props} />;
});
